/**
 * Google Analytics Provider
 * Wrapper around nuxt-gtag — emits legacy clientportal-core GA event names and wrapper fan-out.
 */

import { BaseAnalyticsProvider } from './base.provider'
import { CREATOR_ACTIVITY_LEGACY_EVENTS, LEGACY_WRAPPED_EVENT_NAMES, LegacyGaEventNames } from '../constants/legacy-ga-events.constants'
import { AnalyticsEvents } from '../types/events'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import { sanitizeGaEventName } from '../utils/sanitize-ga-event-name.utils'
import type { AnalyticsEventProperties } from '../types/properties'

/** Catalog event → legacy GA4 event name (kebab-case). */
const GA_EVENT_MAP: Record<string, string> = {
  [AnalyticsEvents.LOGIN_SUCCESS]: LegacyGaEventNames.CUSTOM_LOGIN_EVENT,
  [AnalyticsEvents.LOGOUT]: LegacyGaEventNames.LOGOUT,
  [AnalyticsEvents.POST_CREATED]: LegacyGaEventNames.POST_CREATED,
  [AnalyticsEvents.COMMENT_CREATED]: LegacyGaEventNames.COMMENT_CREATED,
  [AnalyticsEvents.REACTION_ADDED]: LegacyGaEventNames.CREATE_REACTIONS,
  [AnalyticsEvents.REACTION_REMOVED]: LegacyGaEventNames.DELETE_REACTIONS,
  [AnalyticsEvents.GROUP_JOINED]: LegacyGaEventNames.GROUP_JOINED,
  [AnalyticsEvents.LESSON_COMPLETED]: LegacyGaEventNames.LESSON_COMPLETED,
  [AnalyticsEvents.LESSON_STARTED]: LegacyGaEventNames.LESSON_OPENED,
  [AnalyticsEvents.EVENT_CREATED]: LegacyGaEventNames.EVENT_CREATED,
  [AnalyticsEvents.EVENT_RSVP_YES]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.EVENT_RSVP_NO]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.MEDIA_UPLOADED]: LegacyGaEventNames.MEDIA_UPLOADED,
  [AnalyticsEvents.COURSE_VIEWED]: LegacyGaEventNames.COMMUNITY_COURSE_OPENED,
  [AnalyticsEvents.SEARCH_PERFORMED]: LegacyGaEventNames.GOKOLLAB_SEARCH,
  [AnalyticsEvents.SEARCH_RESULT_CLICKED]: LegacyGaEventNames.GOKOLLAB_SEARCH,
  [AnalyticsEvents.CHAT_MESSAGE_SENT]: LegacyGaEventNames.CHAT_MESSAGE_SENT,
  [AnalyticsEvents.CHAT_MESSAGE_FAILED]: LegacyGaEventNames.CHAT_MESSAGE_FAILED,
  [AnalyticsEvents.CHAT_BLOCKED]: LegacyGaEventNames.CHAT_BLOCKED,
  [AnalyticsEvents.CHAT_CREATED]: LegacyGaEventNames.CHAT_CREATED,
  [AnalyticsEvents.GOKOLLAB_HOMEPAGE_LAND]: LegacyGaEventNames.GOKOLLAB_HOMEPAGE_LAND,
  [AnalyticsEvents.GOKOLLAB_LISTING_VIEW]: LegacyGaEventNames.GOKOLLAB_LISTING_VIEW,
  [AnalyticsEvents.GOKOLLAB_LISTING_JOINED]: LegacyGaEventNames.GOKOLLAB_LISTING_JOINED,
  [AnalyticsEvents.GOKOLLAB_COMMUNITIES_VIEW]: LegacyGaEventNames.GOKOLLAB_COMMUNITIES_VIEW,
  [AnalyticsEvents.GOKOLLAB_COURSES_VIEW]: LegacyGaEventNames.GOKOLLAB_COURSES_VIEW,
  [AnalyticsEvents.GOKOLLAB_CREATORS_VIEW]: LegacyGaEventNames.GOKOLLAB_CREATORS_VIEW,
  [AnalyticsEvents.GOKOLLAB_CATEGORY_SWITCH]: LegacyGaEventNames.GOKOLLAB_CATEGORY_SWITCH,
  [AnalyticsEvents.GOKOLLAB_COURSE_CREATE]: LegacyGaEventNames.GOKOLLAB_COURSE_CREATE,
  [AnalyticsEvents.GOKOLLAB_COMMUNITY_CREATE]: LegacyGaEventNames.GOKOLLAB_COMMUNITY_CREATE,
  [AnalyticsEvents.LESSON_OPENED]: LegacyGaEventNames.LESSON_OPENED,
  [AnalyticsEvents.NEXT_LESSON]: LegacyGaEventNames.NEXT_LESSON,
  [AnalyticsEvents.COMMUNITY_COURSE_OPENED]: LegacyGaEventNames.COMMUNITY_COURSE_OPENED,
  [AnalyticsEvents.FOLLOW]: LegacyGaEventNames.FOLLOW,
  [AnalyticsEvents.UNFOLLOW]: LegacyGaEventNames.UNFOLLOW,
  [AnalyticsEvents.COURSE_MENTIONED]: LegacyGaEventNames.COURSE_MENTIONED,
  [AnalyticsEvents.EVENT_MENTIONED]: LegacyGaEventNames.EVENT_MENTIONED,
  [AnalyticsEvents.CHANNEL_MENTIONED]: LegacyGaEventNames.CHANNEL_MENTIONED,
  [AnalyticsEvents.GO_LIVE_SESSION_JOINED]: LegacyGaEventNames.GO_LIVE_SESSION_JOINED,
  [AnalyticsEvents.USER_BANNED]: LegacyGaEventNames.USER_BANNED,
  [AnalyticsEvents.MEDIA_REMOVED]: LegacyGaEventNames.MEDIA_REMOVED,
  [AnalyticsEvents.EVENT_ATTENDEES_VIEWED]: LegacyGaEventNames.EVENT_ATTENDEES_VIEWED,
  [AnalyticsEvents.EVENT_ATTENDEES_LIST_EXPORTED]: LegacyGaEventNames.EVENT_ATTENDEES_LIST_EXPORTED,
  [AnalyticsEvents.MEMBER_VIEWED_OWN_ANSWERS]: LegacyGaEventNames.MEMBER_VIEWED_OWN_ANSWERS,
  [AnalyticsEvents.ADMIN_VIEWED_MEMBER_ANSWERS]: LegacyGaEventNames.ADMIN_VIEWED_MEMBER_ANSWERS,
  [AnalyticsEvents.CERTIFICATE_DOWNLOAD]: LegacyGaEventNames.CERTIFICATE_DOWNLOAD,
  [AnalyticsEvents.EVENT_RSVP]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.PAGE_VIEW]: 'page_view',
  [AnalyticsEvents.ELEMENT_CLICKED]: 'element_clicked',
  [AnalyticsEvents.ERROR_OCCURRED]: 'error_occurred',
}

interface GaEventPayload {
  gaEventName: string
  params: Record<string, unknown>
}

/** Keys never sent to GA4 (legacy parity: pseudonymous user_id only). */
const GA_PII_PARAM_KEYS = new Set(['email', 'phone', 'full_name', 'fullName', 'first_name', 'last_name'])

/** GA events that may fire without user_id (auto-track only). Legacy trackCustomGAEvents requires login. */
const ANONYMOUS_ALLOWED_GA_EVENTS = new Set(['page_view', 'element_clicked', 'error_occurred'])

export class GoogleAnalyticsProvider extends BaseAnalyticsProvider<GaEventPayload> {
  readonly name = 'GoogleAnalytics'

  private gtag: ((...args: unknown[]) => void) | null = null
  private debug = false
  private appEnv = 'development'
  private appName = ''
  private customLoginFired = false

  constructor(debug = false) {
    super()
    this.debug = debug
  }

  setAppEnv(appEnv: string): void {
    this.appEnv = appEnv
  }

  setAppName(appName: string): void {
    this.appName = appName
  }

  private get allowsAnonymousTracking(): boolean {
    return this.appName === 'GOKOLLAB'
  }

  private requiresAuthenticatedUser(gaEventName: string): boolean {
    return !ANONYMOUS_ALLOWED_GA_EVENTS.has(gaEventName)
  }

  setGtag(gtag: (...args: unknown[]) => void): void {
    this.gtag = gtag
    if (this.debug) console.log('[GoogleAnalytics] gtag instance set')
  }

  /** @internal test reset */
  resetSessionFlagsForTests(): void {
    this.customLoginFired = false
  }

  private get canSendToGoogleAnalytics(): boolean {
    return isGoogleAnalyticsSendEnabled(this.appEnv)
  }

  transform(event: string, properties: AnalyticsEventProperties): GaEventPayload | null {
    const gaEventName = GA_EVENT_MAP[event] ?? sanitizeGaEventName(event)
    if (!gaEventName) return null

    const { userId: _userId, contactId, ...rest } = properties
    const params = this.applyLegacyGokollabParamAliases(gaEventName, {
      ...this.toSnakeCase({
        ...this.stripGaPii(rest),
        ...(contactId ? { user_id: contactId } : {}),
      }),
    })

    return { gaEventName, params }
  }

  /** Maps catalog property names to legacy gaAnalytics.ts param keys for GoKollab dashboards. */
  private applyLegacyGokollabParamAliases(gaEventName: string, params: Record<string, unknown>): Record<string, unknown> {
    const result = { ...params }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_SEARCH) {
      if (result.search_text === undefined && result.query !== undefined) {
        result.search_text = result.query
      }
    }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_CATEGORY_SWITCH) {
      if (result.category === undefined) {
        result.category = result.category_slug ?? result.categorySlug ?? 'all'
      }
    }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_LISTING_VIEW || gaEventName === LegacyGaEventNames.GOKOLLAB_LISTING_JOINED) {
      if (result.slug === undefined) {
        result.slug = result.listing_slug ?? result.listingSlug
      }
      if (result.type === undefined) {
        result.type = result.listing_type ?? result.listingType ?? result.entity_type ?? result.entityType
      }
    }

    if (
      gaEventName === LegacyGaEventNames.GOKOLLAB_HOMEPAGE_LAND ||
      gaEventName === LegacyGaEventNames.GOKOLLAB_COMMUNITY_CREATE ||
      gaEventName === LegacyGaEventNames.GOKOLLAB_COURSE_CREATE
    ) {
      if (result.logged_in === undefined && result.loggedIn !== undefined) {
        result.logged_in = result.loggedIn
      }
    }

    return result
  }

  send({ gaEventName, params }: GaEventPayload): void {
    if (!this.gtag) {
      if (this.debug) console.warn('[GoogleAnalytics] gtag not available')
      return
    }

    if (!this.canSendToGoogleAnalytics) {
      console.log('custom ga events', gaEventName)
      return
    }

    const userId = params.user_id
    if (this.requiresAuthenticatedUser(gaEventName) && !userId) {
      return
    }
    if (!userId && !this.allowsAnonymousTracking) {
      return
    }

    if (gaEventName === LegacyGaEventNames.CUSTOM_LOGIN_EVENT) {
      if (this.customLoginFired) {
        return
      }
      this.customLoginFired = true
    }

    if (LEGACY_WRAPPED_EVENT_NAMES.has(gaEventName as (typeof LegacyGaEventNames)[keyof typeof LegacyGaEventNames])) {
      const wrapperEvent = CREATOR_ACTIVITY_LEGACY_EVENTS.has(gaEventName as (typeof LegacyGaEventNames)[keyof typeof LegacyGaEventNames])
        ? LegacyGaEventNames.CREATOR_ACTIVITY_EVENT
        : LegacyGaEventNames.USER_ACTIVITY_EVENT
      this.emitEvent(wrapperEvent, params)
    }

    this.emitEvent(gaEventName, params)
  }

  /** @param contactId CRM contact id — used as GA `user_id` (legacy parity). */
  identify(contactId: string, traits?: Record<string, unknown>): void {
    if (!this.gtag) return

    if (!this.canSendToGoogleAnalytics) {
      if (this.debug) console.log('[GoogleAnalytics] Identify skipped (development):', contactId)
      return
    }

    try {
      const userProperties: Record<string, unknown> = {}
      if (traits?.locationId) userProperties.location_id = traits.locationId
      if (traits?.appName) userProperties.app_name = traits.appName

      this.gtag('set', { user_id: contactId, user_properties: userProperties })
      if (this.debug) console.log('[GoogleAnalytics] User identified:', contactId, userProperties)

      if (contactId && !this.customLoginFired) {
        this.customLoginFired = true
        // Legacy gaAnalytics.ts passes camelCase `appName` on custom-login-event (not snake_cased).
        this.emitEvent(LegacyGaEventNames.CUSTOM_LOGIN_EVENT, {
          appName: traits?.appName,
          user_id: contactId,
        })
      }
    } catch (error) {
      console.error('[GoogleAnalytics] Identify error:', error)
    }
  }

  private stripGaPii(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      if (GA_PII_PARAM_KEYS.has(key)) continue
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      if (GA_PII_PARAM_KEYS.has(snakeKey)) continue
      result[key] = value
    }
    return result
  }

  private emitEvent(gaEventName: string, params: Record<string, unknown>): void {
    if (!this.gtag) return
    try {
      this.gtag('event', gaEventName, params)
      if (this.debug) console.log('[GoogleAnalytics] Event tracked:', gaEventName, params)
    } catch (error) {
      console.error('[GoogleAnalytics] Error:', error)
    }
  }

  private toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(obj)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      result[snakeKey] = obj[key]
    }
    return result
  }
}
